import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaPlay, FaTrophy, FaFire, FaRegStar } from 'react-icons/fa';
import { MdToday } from 'react-icons/md';
import GameCard from '../GameCard/GameCard';
import DailyResetTimer from '../DailyResetTimer/DailyResetTimer';
import DailyStatsSkeleton from './DailyStatsSkeleton';
import DailyTableSkeleton from './DailyTableSkeleton';
import { useAuth } from '../../contexts/AuthContext';
import useGameStats from '../../hooks/useGameStats';
import { getGamesConfig } from '../../config/games';
import { getCentralDateString } from '../../utils/centralTime';
import apiService from '../../services/api';
import SEOHead from '../SEOHead/SEOHead';
import './Daily.scss';

// Use centralized date function directly

const Daily = () => {
  const navigate = useNavigate();
  const [gameData, setGameData] = useState({});
  const [favoriteGames, setFavoriteGames] = useState([]);
  const [todayDate] = useState(getCentralDateString());
  const [isLoading, setIsLoading] = useState(true);

  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  // PROJECT PHOENIX: Use the new useGameStats hook for guest user functionality
  const { getLocalStreak, getLocalBestStreak } = useGameStats();
  
  const games = useMemo(() => getGamesConfig(), []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('favoriteGames');
      if (saved) {
        setFavoriteGames(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading favorite games:', error);
    }
  }, []);

  const saveFavoriteGames = (favorites) => {
    try {
      localStorage.setItem('favoriteGames', JSON.stringify(favorites));
      setFavoriteGames(favorites);
    } catch (error) {
      console.error('Error saving favorite games:', error);
    }
  };

  const toggleFavorite = (gameId) => {
    const newFavorites = favoriteGames.includes(gameId)
      ? favoriteGames.filter(id => id !== gameId)
      : [...favoriteGames, gameId];
    saveFavoriteGames(newFavorites);
  };

  const favoriteGamesList = useMemo(() => {
    return games.filter(game => favoriteGames.includes(game.id));
  }, [games, favoriteGames]);

  // PROJECT PHOENIX: Refactored guest data loading to use new on-demand streak calculations
  const loadGameDataForGuest = useCallback(() => {
    const data = {};
    games.forEach(game => {
      try {
        // Use the new getLocalStreak function for on-demand streak calculation
        const currentStreak = getLocalStreak(game.streakKey);
        
        // Get best streak using the new reliable function
        const bestStreak = getLocalBestStreak(game.streakKey);
        
        // Check if today's daily game is completed
        const dailyData = localStorage.getItem(game.dailyKey);
        const isCompleted = dailyData ? 
          (JSON.parse(dailyData).gameWon && JSON.parse(dailyData).date === todayDate) : 
          false;
        
        data[game.id] = { currentStreak, bestStreak, isCompleted };
      } catch (error) {
        console.error(`Error loading local data for ${game.id}:`, error);
        data[game.id] = { currentStreak: 0, bestStreak: 0, isCompleted: false };
      }
    });
    setGameData(data);
    setIsLoading(false);
  }, [games, todayDate, getLocalStreak, getLocalBestStreak]);

  const loadGameDataForUser = useCallback(async (userId) => {
    try {
      const response = await apiService.get(`/api/daily-game-states/user/${userId}/summary/${todayDate}`);
      const data = await response.json();
      
      // PROJECT PHOENIX: Backend returns { gameData } with all info combined
      const combinedData = {};
      games.forEach(game => {
        const gameInfo = data.gameData[game.id] || { 
          currentStreak: 0, 
          bestStreak: 0, 
          isCompleted: false 
        };
        
        combinedData[game.id] = {
          currentStreak: gameInfo.currentStreak,
          bestStreak: gameInfo.bestStreak,
          isCompleted: gameInfo.isCompleted,
        };
      });
      setGameData(combinedData);
    } catch (error) {
      console.error("Error loading user's daily data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [games, todayDate]);

  useEffect(() => {
    if (authLoading) return;
    
    setIsLoading(true);
    if (isAuthenticated && user) {
      loadGameDataForUser(user.id);
    } else {
      loadGameDataForGuest();
    }
  }, [authLoading, isAuthenticated, user, loadGameDataForUser, loadGameDataForGuest]);

  const handleGameClick = (gameRoute) => {
    navigate(gameRoute);
  };

  const favoriteGameData = favoriteGamesList.map(game => gameData[game.id] || { isCompleted: false });
  const totalCompleted = favoriteGameData.filter(data => data.isCompleted).length;
  const completionPercentage = favoriteGamesList.length > 0 ? Math.round((totalCompleted / favoriteGamesList.length) * 100) : 0;

  const showLoading = authLoading || isLoading;

  // Create structured data for daily challenges page
  const dailyStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Daily Challenges",
    "description": "Play today's daily challenges from all your favorite games. Track your progress and compete with other players!",
    "url": "https://guessnexus.com/daily",
    "isPartOf": {
      "@type": "WebSite",
      "name": "GuessNexus",
      "url": "https://guessnexus.com"
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": "Today's Daily Challenges",
      "itemListElement": favoriteGamesList.map((game, index) => ({
        "@type": "VideoGame",
        "position": index + 1,
        "name": `${game.title} Daily Challenge`,
        "description": game.description,
        "url": `https://guessnexus.com/solve/${game.id}`,
        "applicationCategory": "Game"
      }))
    }
  };

  return (
    <>
      <SEOHead
        title="Daily Challenges"
        description={`🏆 Ready for today's challenge? Play daily puzzles, track your winning streaks, and climb the leaderboard! ${favoriteGamesList.length} fresh challenges waiting - can you solve them all?`}
        canonicalUrl="https://guessnexus.com/daily"
        structuredData={dailyStructuredData}
      />
      <div className="daily-page">
      <div className="daily-page__container">
        <header className="daily-page__header">
          <div className="daily-page__header-icon">
            <MdToday aria-hidden="true" />
          </div>
          <h1 className="daily-page__header-title">Daily Challenges</h1>
          <p className="daily-page__header-date">{new Date(todayDate).toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</p>
        </header>

        <DailyResetTimer />

        {showLoading ? (
          <DailyStatsSkeleton />
        ) : (
          <div className="daily-page__stats">
          <div className="daily-page__stats-card">
            <div className="daily-page__stats-icon">
              <FaCheckCircle aria-hidden="true" />
            </div>
            <div className="daily-page__stats-content">
              <div className="daily-page__stats-number">{totalCompleted}/{favoriteGamesList.length}</div>
              <div className="daily-page__stats-label">Completed Today</div>
            </div>
          </div>
          
          <div className="daily-page__stats-card">
            <div className="daily-page__stats-icon daily-page__stats-icon--progress">
              <div className="daily-page__progress-circle">
                <svg viewBox="0 0 36 36" className="daily-page__circular-chart">
                  <path className="daily-page__circle-bg"
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path className="daily-page__circle"
                    strokeDasharray={`${completionPercentage}, 100`}
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <text x="18" y="20.35" className="daily-page__percentage">{completionPercentage}%</text>
                </svg>
              </div>
            </div>
            <div className="daily-page__stats-content">
              <div className="daily-page__stats-label">Progress</div>
            </div>
          </div>
        </div>
        )}

        <section className="daily-page__table-section">
          {showLoading ? (
            <DailyTableSkeleton />
          ) : (
            <>
              <h2 className="daily-page__section-title">
                <FaTrophy className="daily-page__section-icon" aria-hidden="true" />
                Games Overview
              </h2>
              {favoriteGamesList.length === 0 ? (
            <div className="daily-page__empty-table">
              <FaRegStar className="daily-page__empty-star-icon" />
              <span> Star your favorite games to track your stats here!</span>
            </div>
          ) : (
            <div className="daily-page__table-wrapper">
              <table className="daily-page__table">
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Current Streak</th>
                    <th>Best Streak</th>
                    <th>Today's Challenge</th>
                  </tr>
                </thead>
                <tbody>
                  {favoriteGamesList.map((game) => {
                    const data = gameData[game.id] || { currentStreak: 0, bestStreak: 0, isCompleted: false };
                    return (
                      <tr key={game.id} className={data.isCompleted ? "daily-page__table-row--completed" : "daily-page__table-row"}>
                        <td className="daily-page__table-cell daily-page__table-cell--game-name">{game.title}</td>
                        <td className="daily-page__table-cell daily-page__table-cell--streak">
                          <div className="daily-page__streak-cell">
                            <FaFire className="daily-page__streak-icon" />
                            {data.currentStreak}
                          </div>
                        </td>
                        <td className="daily-page__table-cell daily-page__table-cell--streak">
                          <div className="daily-page__streak-cell">
                            <FaTrophy className="daily-page__streak-icon" aria-hidden="true" />
                            {data.bestStreak}
                          </div>
                        </td>
                        <td className="daily-page__table-cell daily-page__table-cell--status">
                          {data.isCompleted ? (
                            <div 
                              className="daily-page__status-cell daily-page__status-cell--completed"
                              onClick={() => handleGameClick(game.route)}
                            >
                              <FaCheckCircle className="daily-page__status-icon" aria-hidden="true" />
                              Completed
                            </div>
                          ) : (
                            <div 
                              className="daily-page__status-cell daily-page__status-cell--available"
                              onClick={() => handleGameClick(game.route)}
                            >
                              <FaPlay className="daily-page__status-icon" />
                              Available
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
            </>
          )}
        </section>

        <section className="daily-page__games-section">
          <h2 className="daily-page__section-title">
            <FaPlay className="daily-page__section-icon" />
            Available Games
          </h2>
          <div className="daily-page__games-grid">
            {games.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                gameData={gameData[game.id] || { currentStreak: 0, bestStreak: 0, isCompleted: false }}
                isFavorited={favoriteGames.includes(game.id)}
                onToggleFavorite={() => toggleFavorite(game.id)}
                onClick={() => handleGameClick(game.route)}
              />
            ))}
          </div>
        </section>

        <footer className="daily-page__footer">
          <div className="daily-page__footer-tip">
            <FaTrophy className="daily-page__footer-tip-icon" aria-hidden="true" />
            <span>Complete daily challenges to maintain your streaks</span>
          </div>
        </footer>
      </div>
    </div>
    </>
  );
};

export default Daily;